import React, {useEffect, useState} from "react";
import {Box, Checkbox, FormControlLabel, Grid, TextField, Typography} from "@mui/material";

const daysOfWeek = [
    {name: 'monday', label: 'Понедельник', index: 1},
    {name: 'tuesday', label: 'Вторник', index: 2},
    {name: 'wednesday', label: 'Среда', index: 3},
    {name: 'thursday', label: 'Четверг', index: 4},
    {name: 'friday', label: 'Пятница', index: 5},
    {name: 'saturday', label: 'Суббота', index: 6},
    {name: 'sunday', label: 'Воскресенье', index: 0},
];

const getDefaultWorkSchedule = () => {
    const schedule = {};
    daysOfWeek.forEach((day) => {
        const isWeekend = day.index === 0 || day.index === 6; // Воскресенье или суббота
        schedule[day.name] = {
            startTime: isWeekend ? null : '09:00',
            endTime: isWeekend ? null : '18:00',
            isWorking: !isWeekend,
            is24_7: false,
        };
    });
    return schedule;
};

const isValidTime = (time) => /^\d{2}:\d{2}$/.test(time);

const hasPartialOrInvalidEntry = (schedule) => {
    return daysOfWeek.some((day) => {
        const {startTime, endTime, isWorking, is24_7} = schedule[day.name];
        if (!isWorking || is24_7) return false;

        const startValid = isValidTime(startTime || '');
        const endValid = isValidTime(endTime || '');

        // Один есть — другого нет, или хотя бы одно поле — в неверном формате
        return (startTime || endTime) && (!startValid || !endValid);
    });
};

export const WorkScheduleForm = ({formData, setFormData, onErrorChange}) => {
    const [is24_7, setIs24_7] = useState(formData.workSchedule?.is24_7 || false);
    const [workSchedule, setWorkSchedule] = useState(() => {
        if (formData.workSchedule && Object.keys(formData.workSchedule).length > 1) {
            // Если workSchedule есть и не пустой (больше чем просто is24_7)
            return {...formData.workSchedule};
        }
        // Иначе используем дефолтное расписание
        return getDefaultWorkSchedule();
    });

    useEffect(() => {
        console.log(workSchedule)
        const hasError = !is24_7 && hasPartialOrInvalidEntry(workSchedule);
        if (typeof onErrorChange === 'function') {
            onErrorChange(hasError);
        }

        setFormData((prev) => ({
            ...prev,
            workSchedule: {...workSchedule, is24_7},
        }));
    }, [workSchedule, is24_7, setFormData, onErrorChange]);

    const handleTimeChange = (day, field, value) => {
        if (value && !/^\d{2}:\d{2}$/.test(value)) return; // Валидация времени
        setWorkSchedule((prev) => ({
            ...prev,
            [day]: {
                ...prev[day],
                [field]: value || null,
            },
        }));
    };

    const handleCheckboxChange = (day, isWorking) => {
        setWorkSchedule((prev) => ({
            ...prev,
            [day]: {
                ...prev[day],
                isWorking,
                startTime: isWorking ? prev[day].startTime : null,
                endTime: isWorking ? prev[day].endTime : null,
                is24_7: isWorking ? prev[day].is24_7 : false,
            },
        }));
    };

    const handleDay24_7Change = (day, is24_7) => {
        setWorkSchedule((prev) => ({
            ...prev,
            [day]: {
                ...prev[day],
                is24_7,
                startTime: is24_7 ? null : prev[day].startTime,
                endTime: is24_7 ? null : prev[day].endTime,
            },
        }));
    };

    const isTimeInvalid = (dayName) => {
        const {startTime, endTime, isWorking, is24_7} = workSchedule[dayName];
        if (!isWorking || is24_7) return false;

        const hasStart = !!startTime;
        const hasEnd = !!endTime;

        return (hasStart && !hasEnd) || (!hasStart && hasEnd);
    };


    return (
        <Box sx={{mt: 2, width: '100%'}}>
            <Typography variant="h6" gutterBottom>
                Расписание работы
            </Typography>
            <Box sx={{mb: 3}}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={is24_7}
                            onChange={(e) => setIs24_7(e.target.checked)}
                            color="primary"
                        />
                    }
                    label="Филиал работает круглосуточно (24/7)"
                />
            </Box>
            {daysOfWeek.map((day) => (
                <Grid container spacing={2} key={day.name} sx={{mb: 2, alignItems: 'center'}}>
                    <Grid item xs={12} sm={3}>
                        <Typography>{day.label}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={2.5}>
                        <TextField
                            label="Время начала"
                            type="time"
                            value={workSchedule[day.name].startTime || ''}
                            onChange={(e) => handleTimeChange(day.name, 'startTime', e.target.value)}
                            disabled={!workSchedule[day.name].isWorking || is24_7 || workSchedule[day.name].is24_7}
                            InputLabelProps={{shrink: true}}
                            inputProps={{step: 300}}
                            fullWidth
                            error={!is24_7 && isTimeInvalid(day.name)}
                            helperText={
                                !is24_7 && isTimeInvalid(day.name) ? 'Заполните оба поля или оставьте пустыми' : ''
                            }
                        />
                    </Grid>
                    <Grid item xs={12} sm={2.5}>
                        <TextField
                            label="Время окончания"
                            type="time"
                            value={workSchedule[day.name].endTime || ''}
                            onChange={(e) => handleTimeChange(day.name, 'endTime', e.target.value)}
                            disabled={!workSchedule[day.name].isWorking || is24_7 || workSchedule[day.name].is24_7}
                            InputLabelProps={{shrink: true}}
                            inputProps={{step: 300}}
                            fullWidth
                            error={!is24_7 && isTimeInvalid(day.name)}
                            helperText={
                                !is24_7 && isTimeInvalid(day.name) ? 'Заполните оба поля или оставьте пустыми' : ''
                            }
                        />
                    </Grid>
                    <Grid item xs={12} sm sx={{flexGrow: 1}}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    sx={{padding: 0.5}}
                                    checked={workSchedule[day.name].is24_7}
                                    onChange={(e) => handleDay24_7Change(day.name, e.target.checked)}
                                    disabled={!workSchedule[day.name].isWorking || is24_7}
                                />
                            }
                            label="Круглосуточно (с 00:00 до 23:59)"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    sx={{padding: 0.5}}
                                    checked={!workSchedule[day.name].isWorking}
                                    onChange={(e) => handleCheckboxChange(day.name, !e.target.checked)}
                                    disabled={is24_7}
                                />
                            }
                            label="Выходной"
                        />
                    </Grid>
                </Grid>
            ))}
        </Box>
    );
};
